import type { Gym, MessageMetric } from "@api/types";
import { ContextSwitcher } from "@components/context-switcher";
import { UsageGauge } from "@ui/components/usage-gauge";
import { NavLink } from "@ui/elements";
import classNames from "classnames";
import { Settings } from "lucide-react";
import Link from "next/link";
import { useRouter } from "next/router";
import type { ReactNode } from "react";
import { useState } from "react";
import { Logo } from "@/components/logo";
import { Hotspot } from "@/components/walkthrough";
import type { SubNavigationItem } from "@/lib/constants/app-navigation";

interface NavigationGroup {
  title: string;
  items: Array<{
    name: string;
    href: string;
    icon: React.ComponentType<any>;
    current?: boolean;
    count?: number;
    placeholder?: boolean;
    hotspot?: { id: string; title: string; body: string };
  }>;
}

export type SecondaryNavMode = "settings" | "analytics" | "assistant";
type NavMode = "main" | SecondaryNavMode;

// Pages Router remounts the layout on every navigation, so track the last nav
// mode at module level. The slide animation should only play when crossing
// modes (main <-> settings/analytics), not on every in-section navigation.
let lastNavMode: NavMode = "main";

interface SidebarNavigationProps {
  groups: NavigationGroup[];
  subItems?: SubNavigationItem[];
  gym?: Gym | null;
  metrics?: MessageMetric[];
  /**
   * When set, this panel slides in from the right over the main nav body
   * (Vercel-style secondary nav). The logo header and org/gym switcher stay
   * mounted above it.
   */
  secondaryNav?: ReactNode;
  secondaryNavMode?: SecondaryNavMode;
}

function SidebarNavigation(props: SidebarNavigationProps) {
  const router = useRouter();

  const mode: NavMode = props.secondaryNav ? (props.secondaryNavMode ?? "settings") : "main";
  // Capture once on mount: animate only when the nav mode changed since the
  // previously rendered page.
  const [animate] = useState(() => {
    const changed = lastNavMode !== mode;
    lastNavMode = mode;
    return changed;
  });

  const openSettings = () => {
    router.push("/settings/inbox/business-info");
  };

  return (
    <div className="bg-sidebar border-border flex h-full min-h-0 flex-col overflow-hidden border-r">
      {/* Fixed Header — persists across main nav and secondary (settings /
          analytics) nav so the logo and org/gym scope switcher never move. */}
      <div className="flex-shrink-0">
        <div className="flex items-center justify-between px-4 py-5">
          <Link href="/messages">
            <Logo className="h-7 w-auto" />
          </Link>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={openSettings}
              className="text-muted-foreground hover:text-foreground transition-colors"
              title="Settings"
              aria-label="Open settings"
            >
              <Settings className="size-4" />
            </button>
          </div>
        </div>
        <div className="flex items-center justify-center px-4 pb-4">
          <ContextSwitcher type="gyms" />
        </div>
      </div>

      {/* Sliding nav body: main nav stays mounted beneath while the secondary
          panel covers it, so entering settings/analytics reads as a slide-over
          rather than a replacement. */}
      <div className="relative flex-1 min-h-0 overflow-hidden">
        <div
          aria-hidden={!!props.secondaryNav}
          className={classNames(
            "h-full overflow-y-auto min-h-0",
            !!props.secondaryNav && "pointer-events-none",
            // Matches the contact sidebar view transition (20px slide + fade,
            // 150ms ease-out) so panel navigation feels consistent app-wide.
            !props.secondaryNav &&
              animate &&
              "animate-in fade-in slide-in-from-left-5 duration-150 ease-out",
          )}
        >
          <nav className="px-2 pb-4 space-y-3">
            {props.groups.map((group) => (
              <div key={group.title} className="space-y-1">
                <h3 className="px-2 text-[0.625rem] font-medium text-muted-foreground uppercase tracking-wide">
                  {group.title}
                </h3>
                <div className="space-y-1">
                  {group.items.map((item) => (
                    <div key={item.href} className="relative">
                      {item.placeholder ? (
                        <span
                          aria-disabled="true"
                          className="text-muted-foreground group flex cursor-default items-center rounded-md px-2 py-1.5 text-sm font-medium relative"
                        >
                          <item.icon
                            className="text-muted-foreground mr-3 h-4 w-4 flex-shrink-0"
                            aria-hidden="true"
                          />
                          <span>{item.name}</span>
                        </span>
                      ) : (
                        <NavLink
                          href={item.href}
                          className={classNames(
                            item.current
                              ? "bg-muted"
                              : "text-muted-foreground hover:text-foreground/80 hover:bg-muted/80",
                            "group flex items-center px-2 py-1.5 text-sm font-medium rounded-md relative",
                          )}
                        >
                          <span
                            className={classNames(
                              item.current ? "block" : "hidden",
                              "absolute inset-0 -left-2 right-auto rounded-r-md bg-brand-accent h-full w-1",
                            )}
                          />
                          <item.icon
                            className={classNames(
                              item.current
                                ? "text-foreground"
                                : "text-muted-foreground group-hover:text-foreground/80",
                              "mr-3 flex-shrink-0 h-4 w-4",
                            )}
                            aria-hidden="true"
                          />
                          <span>{item.name}</span>
                          {item.count ? (
                            <span className="inline-block items-center justify-center py-0.5 px-3 ml-auto text-xs text-foreground bg-blue-100 dark:bg-brand-accent/15 dark:group-hover:bg-brand-accent/15 rounded-full">
                              {Number(item.count) > 20 ? "20+" : Number(item.count)}
                            </span>
                          ) : null}
                        </NavLink>
                      )}
                      {/* Sibling of the link, not a child, so opening the callout
                        does not navigate. Straddles the link's right edge, in the
                        nav gutter past the count badge, so the row's layout is the
                        same whether or not the dot is showing. */}
                      {item.hotspot ? (
                        <Hotspot
                          id={item.hotspot.id}
                          title={item.hotspot.title}
                          body={item.hotspot.body}
                          side="right"
                          className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2"
                        />
                      ) : null}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </nav>
          <nav className="border-border px-2 py-4 border-t">
            {props.subItems?.map((item) => {
              if (item.href === "/settings") {
                return (
                  <button
                    type="button"
                    key={item.href}
                    onClick={openSettings}
                    className={classNames(
                      "text-muted-foreground hover:text-foreground/80 hover:bg-muted/80",
                      "group flex items-center px-2 py-1 text-xs font-normal rounded-md relative w-full text-left",
                    )}
                  >
                    <item.icon
                      className="text-muted-foreground group-hover:text-foreground/80 mr-2 flex-shrink-0 h-3.5 w-3.5"
                      aria-hidden="true"
                    />
                    <span>{item.name}</span>
                  </button>
                );
              }
              if (item.onClick) {
                return (
                  <button
                    type="button"
                    key={item.name}
                    onClick={item.onClick}
                    className={classNames(
                      "text-muted-foreground hover:text-foreground/80 hover:bg-muted/80",
                      "group flex items-center px-2 py-1 text-xs font-normal rounded-md relative w-full text-left",
                    )}
                  >
                    <item.icon
                      className="text-muted-foreground group-hover:text-foreground/80 mr-2 flex-shrink-0 h-3.5 w-3.5"
                      aria-hidden="true"
                    />
                    <span>{item.name}</span>
                  </button>
                );
              }
              return (
                <NavLink
                  key={item.href}
                  href={item.href}
                  className={classNames(
                    item.current
                      ? "bg-muted"
                      : "text-muted-foreground hover:text-foreground/80 hover:bg-muted/80",
                    "group flex items-center px-2 py-1 text-xs font-normal rounded-md relative",
                  )}
                >
                  <item.icon
                    className={classNames(
                      item.current
                        ? "text-foreground"
                        : "text-muted-foreground group-hover:text-foreground/80",
                      "mr-2 flex-shrink-0 h-3.5 w-3.5",
                    )}
                    aria-hidden="true"
                  />
                  <span>{item.name}</span>
                </NavLink>
              );
            })}
          </nav>
        </div>
        {props.secondaryNav && (
          <div
            className={classNames(
              "absolute inset-0 bg-sidebar overflow-y-auto",
              // Same 20px slide + fade as the contact sidebar view transition.
              animate && "animate-in fade-in slide-in-from-right-5 duration-150 ease-out",
            )}
          >
            {props.secondaryNav}
          </div>
        )}
      </div>

      {/* Fixed Footer */}
      <div className="flex-shrink-0 flex flex-col">
        <div className="px-4 pb-2">
          <UsageGauge />
        </div>
        <small className="text-center px-4 mt-1 mb-2 hidden text-[0.5rem] opacity-50 lg:block">
          © {new Date().getFullYear()}{" "}
          <a
            target="_blank"
            rel="noreferrer"
            className="hover:underline"
            href="https://sendhelios.com"
          >
            Helios CRM, Inc.
          </a>
        </small>
      </div>
    </div>
  );
}

export default SidebarNavigation;
